import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { SoftwareIcon } from '@common/utils/icons/icons';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import MessageType from '@common/enums/robot/rsw/message-types';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectRobotsData, selectTorizonData } from '../../../store/robots/robots-slice-selectors';
import { getTorizonStatus, postTorizonUpdate } from '../../../store/robots/robots-actions';
import { setTorizonReadinessPending } from '../../../store/robots/robots-slice';
import { sendRobotWebsocketMessage } from '../../../store/middleware/robot-websocket-middleware';
import OsVariant from '../../../models/enums/os-variant';
import TorizonUpdateStatus from '../../../models/enums/torizon-update-status';
import WebSocketConnectionStatus from '../../../models/enums/websocket-connection-status';

const READINESS_TIMEOUT_MS = 30000;

const readinessCheckKey = (check: string) =>
  `pre-update-check-${check.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())}`;

const TorizonUpdateCard: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const torizon = useAppSelector((state) => selectTorizonData(state));

  const robot = robotsData.currentRobot;
  const isTorizon = robot?.osVariant === OsVariant.TORIZON;
  const isRobotOnline = robot?.status?.active === true;

  const updateStatus = robot?.status?.torizonUpdateStatus;
  const isUpdating = updateStatus === TorizonUpdateStatus.IN_PROGRESS;
  const isPending = updateStatus === TorizonUpdateStatus.PENDING;
  const showReadiness = torizon.status?.updateAvailable === true && !isUpdating;

  const [readinessTimedOut, setReadinessTimedOut] = useState(false);

  const isHubConnected = robotsData.robotHubState === WebSocketConnectionStatus.CONNECTED;

  const requestReadiness = React.useCallback(() => {
    if (robot?.id == null || !isRobotOnline || !isHubConnected) return;
    setReadinessTimedOut(false);
    dispatch(setTorizonReadinessPending(true));
    dispatch(sendRobotWebsocketMessage({ type: MessageType.TorizonUpdateReady, message: null }));
  }, [robot?.id, isRobotOnline, isHubConnected, dispatch]);

  useEffect(() => {
    // isTorizon guards both branches: this card's effects run for every robot
    // regardless of osVariant, and a stale torizonUpdateStatus on a non-Torizon robot
    // must not trigger a readiness request.
    if (isTorizon && showReadiness && !isPending) {
      requestReadiness();
    }
  }, [isTorizon, showReadiness, isPending, requestReadiness, updateStatus]);

  useEffect(() => {
    if (isTorizon && robot?.id != null) {
      dispatch(getTorizonStatus(robot.id));
    }
  }, [dispatch, isTorizon, robot?.id, updateStatus]);

  useEffect(() => {
    setReadinessTimedOut(false);
  }, [torizon.readiness]);

  useEffect(() => {
    if (!torizon.readinessPending) return;
    const timer = setTimeout(() => {
      setReadinessTimedOut(true);
      dispatch(setTorizonReadinessPending(false));
    }, READINESS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [dispatch, torizon.readinessPending]);

  if (!isTorizon || robot == null) return null;

  const status = torizon.status;

  const readinessErrors = torizon.readiness?.errors ?? [];
  const readinessWarnings = torizon.readiness?.warnings ?? [];

  // The shared pre-update-check translations start with a "- " bullet, which the alert header doesn't want.
  const translateReadinessCheck = (check: string) => t(readinessCheckKey(check)).replace(/^- /, '');

  // Mirrors the if/else tooltip cascade in robotics_software/web's UpdateButtons --
  // one reason at a time, most fundamental first, so a disabled button always
  // explains itself instead of leaving the user to guess which condition failed.
  // The readiness check is advisory only: its result is shown, but never blocks a start.
  let buttonDisabled = true;
  let buttonTooltip: string | undefined;

  if (isUpdating) {
    buttonTooltip = t('update-button-info-update-in-progress');
  } else if (isPending) {
    buttonTooltip = t('info-torizon-update-queued');
  } else if (
    torizon.rest.getStatus === HttpFetchStatus.Loading ||
    torizon.rest.updateStatus === HttpFetchStatus.Loading ||
    torizon.rest.cancelStatus === HttpFetchStatus.Loading
  ) {
    buttonTooltip = t('update-button-info-fetching-update-info');
  } else if (!isRobotOnline) {
    buttonTooltip = t('error-torizon-robot-offline');
  } else if (status?.error != null) {
    buttonTooltip = t(status.error);
  } else if (status?.versionSetName == null) {
    buttonTooltip = t('info-torizon-no-version-set');
  } else if (status?.updateAvailable !== true) {
    buttonTooltip = t('update-button-info-software-up-to-date');
  } else {
    buttonDisabled = false;
  }

  const updateClickHandler = () => {
    dispatch(postTorizonUpdate(robot.id));
  };

  return (
    <CustomSplitColumnCard
      title={t('title-update')}
      content={[
        {
          left: (
            <Stack pr={2} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1} py={1}>
                <SoftwareIcon />
                <Typography>{t('title-update')}</Typography>
              </Stack>
              <CustomAlert header={t('info-torizon-update-description')} severity="info" />
            </Stack>
          ),
          right: (
            <Stack spacing={2} pt={1}>
              <Stack spacing={1}>
                <InputLabel>{t('label-build-packages')}</InputLabel>
                {status?.error != null ? (
                  <CustomAlert severity="error" header={t(status.error)} />
                ) : status == null ? (
                  <CustomAlert
                    severity="info"
                    header={t('info-torizon-checking-update-availability')}
                    icon={<CircularProgress size={24} data-testid="torizon-status-spinner" />}
                  />
                ) : status.versionSetName == null ? (
                  <CustomAlert severity="info" header={t('info-torizon-no-version-set')} />
                ) : (
                  <CustomAlert
                    severity={status.updateAvailable ? 'info' : 'success'}
                    header={t(
                      status.updateAvailable
                        ? 'info-torizon-can-update-to-set'
                        : 'info-torizon-up-to-date-on-set',
                      { name: status.versionSetName },
                    )}
                  />
                )}
              </Stack>

              {updateStatus === TorizonUpdateStatus.FAILURE && (
                <Stack spacing={1}>
                  <InputLabel>{t('label-last-update-result')}</InputLabel>
                  <CustomAlert
                    data-testid="torizon-last-update-result"
                    severity="error"
                    header={t('error-torizon-last-update-failed')}
                  />
                </Stack>
              )}

              {showReadiness && (
                <Stack spacing={1}>
                  <InputLabel>{t('label-robot-ready')}</InputLabel>
                  {!isRobotOnline && (
                    <CustomAlert severity="error" header={t('error-torizon-robot-offline')} />
                  )}

                  {isRobotOnline && torizon.readinessPending && (
                    <CustomAlert
                      header={t('info-torizon-checking-readiness')}
                      severity="info"
                      icon={<CircularProgress size={24} data-testid="torizon-readiness-spinner" />}
                    />
                  )}

                  {isRobotOnline &&
                    !torizon.readinessPending &&
                    !readinessTimedOut &&
                    torizon.readiness?.ready === true && (
                      <CustomAlert
                        data-testid="torizon-readiness-confirmed"
                        header={t('info-torizon-readiness-confirmed')}
                        severity="success"
                      />
                    )}

                  {isRobotOnline && readinessTimedOut && (
                    <Stack spacing={1}>
                      <CustomAlert
                        severity="warning"
                        header={t('error-torizon-readiness-timeout')}
                      />
                      <Button
                        data-testid="button-torizon-readiness-retry"
                        onClick={requestReadiness}
                      >
                        {t('button-retry')}
                      </Button>
                    </Stack>
                  )}

                  {readinessWarnings.map((warning) => (
                    <CustomAlert
                      key={warning}
                      severity="warning"
                      header={translateReadinessCheck(warning)}
                    />
                  ))}
                  {readinessErrors.map((error) => (
                    <CustomAlert
                      key={error}
                      severity="error"
                      header={translateReadinessCheck(error)}
                    />
                  ))}
                </Stack>
              )}

              {isPending && (
                <CustomAlert
                  data-testid="torizon-update-pending"
                  severity="info"
                  header={t('info-torizon-update-queued')}
                />
              )}

              <Tooltip title={buttonTooltip}>
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    data-testid="button-torizon-update"
                    disabled={buttonDisabled}
                    onClick={updateClickHandler}
                  >
                    {isUpdating || isPending ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress size={24} />
                        <Typography variant="inherit">
                          {t(isUpdating ? 'info-torizon-updating' : 'button-torizon-update-queued')}
                        </Typography>
                      </Stack>
                    ) : (
                      t('button-torizon-update')
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          ),
        },
      ]}
    />
  );
};

export default TorizonUpdateCard;
